import { getFontSized } from '@tamagui/get-font-sized'
import { getSpace } from '@tamagui/get-token'
import type { SizeVariantSpreadFunction } from '@tamagui/web'
import { useState } from 'react'
import type { ColorTokens, FontSizeTokens } from 'tamagui'
import {
  Input,
  Label,
  Button as TButton,
  Input as TInput,
  Text,
  View,
  XGroup,
  createStyledContext,
  getFontSize,
  getVariable,
  isWeb,
  styled,
  useGetThemedIcon,
  useTheme,
  withStaticProperties,
} from 'tamagui'
import './inputParts.css'

const defaultContextValues = {
  size: '$true',
  scaleIcon: 1.2,
  color: undefined,
} as const

export const InputContext = createStyledContext<{
  size: FontSizeTokens
  scaleIcon: number
  color?: ColorTokens | (string & {})
}>(defaultContextValues)

export const defaultInputGroupStyles = {
  size: '$true',
  fontFamily: '$body',
  borderWidth: 1,
  outlineWidth: 0,
  color: '$color',

  ...(isWeb
    ? {
        tabIndex: 0,
      }
    : {
        focusable: true,
      }),

  borderColor: '$borderColor',
  backgroundColor: '$color2',

  // this fixes a flex bug where it overflows container
  minWidth: 0,

  hoverStyle: {
    borderColor: '$borderColorHover',
  },

  focusStyle: {
    outlineColor: '$outlineColor',
    outlineWidth: 2,
    outlineStyle: 'solid',
    borderColor: '$borderColorFocus',
  },
} as const

const InputGroupFrame = styled(XGroup, {
  justifyContent: 'space-between',
  context: InputContext,
  variants: {
    unstyled: {
      false: defaultInputGroupStyles,
    },
    scaleIcon: {
      // @ts-expect-error tamagui typescript weirdness
      ':number': {},
    },
    applyFocusStyle: {
      ':boolean': (val, { props }) => {
        if (val) {
          return props.focusStyle || defaultInputGroupStyles.focusStyle
        }
      },
    },
    size: {
      // @ts-expect-error tamagui typescript weirdness
      '...size': (val, { tokens }) => ({
        // @ts-expect-error tamagui typescript weirdness
        borderRadius: tokens.radius[val],
      }),
    },
  } as const,
  defaultVariants: {
    // @ts-expect-error tamagui typescript weirdness
    unstyled: process.env.TAMAGUI_HEADLESS === '1',
  },
})

const FocusContext = createStyledContext({
  setFocused: (_val: boolean) => {},
  focused: false,
})

const InputGroupImpl = InputGroupFrame.styleable((props, forwardedRef) => {
  const { children, ...rest } = props
  const [focused, setFocused] = useState(false)

  return (
    <FocusContext.Provider focused={focused} setFocused={setFocused}>
      <InputGroupFrame
        // @ts-expect-error tamagui typescript weirdness
        applyFocusStyle={focused}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </InputGroupFrame>
    </FocusContext.Provider>
  )
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const inputSizeVariant: SizeVariantSpreadFunction<any> = (val = '$true', extras) => {
  // @ts-expect-error tamagui typescript weirdness
  const radiusToken = extras.tokens.radius[val] ?? extras.tokens.radius['$true']
  const paddingHorizontal = getSpace(val, {
    shift: -1,
    bounds: [2],
  })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
  const fontStyle = getFontSized(val as any, extras)
  // lineHeight messes up input on native
  if (!isWeb && fontStyle) {
    delete fontStyle['lineHeight']
  }
  return {
    ...fontStyle,
    height: val,
    borderRadius: extras.props.circular ? 100_000 : radiusToken,
    paddingHorizontal,
  }
}

const InputFrame = styled(TInput, {
  unstyled: true,
  context: InputContext,
})

const InputImpl = InputFrame.styleable((props, ref) => {
  const { setFocused } = FocusContext.useStyledContext()
  const { size } = InputContext.useStyledContext()
  const { ...rest } = props
  return (
    <View flex={1}>
      <Input
        ref={ref}
        size={size}
        {...rest}
        onFocus={(...args) => {
          setFocused(true)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          rest.onFocus?.(...args)
        }}
        onBlur={(...args) => {
          setFocused(false)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          rest.onBlur?.(...args)
        }}
        className="tamagui-input-no-outline"
      />
    </View>
  )
})

const InputSection = styled(XGroup.Item, {
  // @ts-expect-error tamagui typescript weirdness
  justifyContent: 'center',
  alignItems: 'center',
  context: InputContext,
})

const Button = styled(TButton, {
  context: InputContext,
  // @ts-expect-error tamagui typescript weirdness
  justifyContent: 'center',
  alignItems: 'center',

  variants: {
    size: {
      '...size': (val = '$true', { tokens }) => {
        if (typeof val === 'number') {
          return {
            paddingHorizontal: 0,
            height: val,
            borderRadius: val * 0.2,
          }
        }
        return {
          paddingHorizontal: 0,
          height: val,
          // @ts-expect-error tamagui typescript weirdness
          borderRadius: tokens.radius[val],
        }
      },
    },
  } as const,
})

// Icon starts

export const InputIconFrame = styled(View, {
  // @ts-expect-error tamagui typescript weirdness
  justifyContent: 'center',
  alignItems: 'center',
  context: InputContext,

  variants: {
    size: {
      '...size': (val, { tokens }) => ({
        // @ts-expect-error tamagui typescript weirdness
        paddingHorizontal: tokens.space[val],
      }),
    },
  } as const,
})

const getIconSize = (size: FontSizeTokens, scale: number) =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  (typeof size === 'number' ? size * 0.5 : getFontSize(size as FontSizeTokens)) * scale

const InputIcon = InputIconFrame.styleable<{
  scaleIcon?: number
  color?: ColorTokens | (string & {})
}>((props, ref) => {
  const { children, color: colorProp, ...rest } = props
  const inputContext = InputContext.useStyledContext()
  const { size = '$true', color: contextColor, scaleIcon = 1 } = inputContext

  const theme = useTheme()
  const color = getVariable(
    // @ts-expect-error tamagui typescript weirdness
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unnecessary-condition
    contextColor || theme[contextColor]?.get('web') || theme.color10?.get('web'),
  )
  const iconSize = getIconSize(size, scaleIcon)

  const getThemedIcon = useGetThemedIcon({ size: iconSize, color })
  return (
    <InputIconFrame ref={ref} {...rest}>
      {getThemedIcon(children)}
    </InputIconFrame>
  )
})

export const InputContainerFrame = styled(View, {
  context: InputContext,
  flexDirection: 'column',

  variants: {
    size: {
      '...size': (val, { tokens }) => ({
        // @ts-expect-error tamagui typescript weirdness
        gap: tokens.space[val].val * 0.3,
      }),
    },
    color: {
      '...color': () => ({}),
    },
    gapScale: {
      // @ts-expect-error tamagui typescript weirdness
      ':number': {},
    },
  } as const,

  defaultVariants: {
    // @ts-expect-error tamagui typescript weirdness
    size: '$4',
  },
})

export const InputLabel = styled(Label, {
  context: InputContext,
  variants: {
    size: {
      // @ts-expect-error tamagui typescript weirdness
      '...fontSize': getFontSized,
    },
  } as const,
})

export const InputInfo = styled(Text, {
  context: InputContext,
  color: '$color10',

  variants: {
    size: {
      '...fontSize': (val, { font }) => {
        if (!font) return
        const fontSize = font.size[val].val * 0.8
        const lineHeight = font.lineHeight?.[val].val * 0.8
        const fontWeight = font.weight?.['$2']
        const letterSpacing = font.letterSpacing?.[val]
        const textTransform = font.transform?.[val]
        const fontStyle = font.style?.[val]
        return {
          fontSize,
          lineHeight,
          fontWeight,
          letterSpacing,
          textTransform,
          fontStyle,
        }
      },
    },
  } as const,
})

const InputXGroup = styled(XGroup, {
  context: InputContext,

  variants: {
    size: {
      '...size': (val, { tokens }) => {
        // @ts-expect-error tamagui typescript weirdness
        const radiusToken = tokens.radius[val] ?? tokens.radius['$true']
        return {
          borderRadius: radiusToken,
        }
      },
    },
  } as const,
})

export const InputParts = withStaticProperties(InputContainerFrame, {
  Box: InputGroupImpl,
  Area: InputImpl,
  Section: InputSection,
  Button,
  Icon: InputIcon,
  Info: InputInfo,
  Label: InputLabel,
  XGroup: withStaticProperties(InputXGroup, { Item: XGroup.Item }),
})
